import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { useState } from 'react';

import { DataTable } from 'src/components/DataTable';
import { AppPagination } from 'src/components/Pagination';
import { colors } from 'src/global/styles/colors';
import { useProjectDashboardContext } from 'src/hooks/useProjectDashboardContext';
import { useGetProjectRolesQuery } from 'src/store/services/project-dashboard.service';

const RoleListTable = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { projectId, address, network } = useProjectDashboardContext();

  const {
    data: rolesResponse,
    isLoading,
    error,
  } = useGetProjectRolesQuery(
    {
      projectId,
      page,
      size: limit,
      address,
      network,
    },
    { skip: !projectId || !address || !network },
  );

  const roles = rolesResponse?.data || [];
  const meta = rolesResponse?.meta;

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage + 1);
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <Box
      sx={{
        background: colors.neutral03,
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        padding: '24px',
      }}>
      <Stack spacing={3}>
        {/* Header */}
        <Typography
          sx={{
            color: colors.white,
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '24px',
          }}>
          Role List
        </Typography>

        {/* Table */}
        <DataTable
          isEmpty={!roles.length && !isLoading}
          skeletonRowCount={3}
          isLoading={isLoading}
          renderHeader={() => (
            <TableRow
              sx={{
                backgroundColor: colors.neutral06,
              }}>
              <TableCell sx={{ paddingLeft: '16px', width: '40px' }}>#</TableCell>
              <TableCell sx={{ width: '35%' }}>Wallet Address</TableCell>
              <TableCell>Role</TableCell>
            </TableRow>
          )}
          renderBody={() => (
            <>
              {error && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ color: colors.red200 }}>Failed to load roles data</Typography>
                  </TableCell>
                </TableRow>
              )}

              {!error && roles.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ color: colors.neutral90 }}>No roles available</Typography>
                  </TableCell>
                </TableRow>
              )}

              {roles.map((row, index) => (
                <TableRow key={`${row.addr}-${index}`}>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Typography fontSize={'14px'}>{(page - 1) * limit + index + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction='row' justifyContent={'space-between'} alignItems='center' spacing={1}>
                      <Typography sx={{ fontSize: '14px' }}>{formatAddress(row.addr)}</Typography>
                      <IconButton
                        size='small'
                        onClick={() => handleCopyAddress(row.addr)}
                        sx={{
                          color: colors.white,
                          '&:hover': {
                            backgroundColor: colors.neutral015,
                          },
                        }}>
                        <ContentCopyIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '14px' }}>{row.role}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </>
          )}
        />

        {meta && meta.total > 0 && (
          <Stack direction={'row'} justifyContent={'end'} marginTop={2}>
            <AppPagination
              sx={{
                border: 'none',
              }}
              count={meta.total}
              page={page - 1}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default RoleListTable;
